import type { FeatureLike } from 'ol/Feature';
import {
  Fill,
  Stroke,
  Style,
  Text,
} from 'ol/style';

import { defaultTextToolFontSize } from '#nora-ui/map/drawtools/stores/text-tool-store';
import type { FeatureStyleProperties } from '#nora-ui/map/feature-properties';
import { getFeatureProperties } from '#nora-ui/map/feature-properties';
import { defaultFontStyle } from '#nora-ui/map/map/DrawToolUIManager';

const textToolFontFamily = 'Helvetica,sans-serif';

const defaultTextToolBackgroundColor = '#1C313F';
const defaultTextToolColor = '#AABECB';

/**
 * Takes an input value from the text tool UI and returns a CSS font style string for the text.
 * Returns a font style string in the format of "16px Helvetica,sans-serif"
 * @param fontSize - the text font size as a number.
 */
export function createTextToolFontStyle(fontSize: number): string {
  return `${fontSize}px ${textToolFontFamily}`;
}

/**
 * Take the CSS font style string, parses it, and returns the numeric font size.
 * Falls back to default if font size is missing.
 * Expected string format is "16px Helvetica,sans-serif", and will break if this format differs.
 * @param font - the CSS font style string.
 */
export function getFontSize(font?: string): number {
  if (font === undefined) {
    return defaultTextToolFontSize;
  }

  const fontSize = Number.parseInt(font, 10);
  if (Number.isNaN(fontSize)) {
    return defaultTextToolFontSize;
  }

  return fontSize;
}

/**
 * Creates ol styles with size and background. Uses extra background stroke for rounded corners.
 * Similar workaround used in marker feature style (https://github.com/openlayers/openlayers/issues/11013).
 * @param feature - text tool feature.
 */
export function createTextToolStyle(feature: FeatureLike): Style[] {
  const { labelText, font } = getFeatureProperties<FeatureStyleProperties>(feature);
  const text = labelText ?? '';
  const textFont = font ?? defaultFontStyle;

  return [
    new Style({
      text: new Text({
        text,
        font: textFont,
        fill: new Fill({
          color: 'rgba(0, 0, 0, 0)',
        }),
        backgroundStroke: new Stroke({
          color: defaultTextToolBackgroundColor,
          width: 8,
          lineCap: 'round',
          lineJoin: 'round',
        }),
        padding: [0, 1, 0, 1],
      }),
    }),
    new Style({
      text: new Text({
        text,
        font: textFont,
        fill: new Fill({
          color: defaultTextToolColor,
        }),
        backgroundFill: new Fill({
          color: defaultTextToolBackgroundColor,
        }),
        padding: [0, 1, 0, 1],
      }),
    }),
  ];
}
